import React, { useState, useEffect } from "react";
import { HiChevronLeft as HiChevronLeftSolid } from "react-icons/hi";
import { QuestionMarkCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import Header from "../../components/layout/Header";
import { collection, doc, getDoc, query, where, getDocs, Timestamp } from "firebase/firestore";
import { db } from "../../firebase/config";
import { getFunctions, httpsCallable } from "firebase/functions";
import Toast from "../../components/Toast";
import LoaderBackdrop from "../../components/LoaderBackdrop";

type ToastType = "success" | "error" | "warning" | "info";

interface ReportStatusOption {
  value: string;
  label: string;
  color: "blue" | "gray" | "red" | "green" | "orange" | "purple";
}

interface ReportOption {
  value: string;
  label: string;
}

interface AppealData {
  note: string;
  submittedAt: Timestamp;
  status: string;
}

interface MyReport {
  id: string;
  postId: string;
  reportedUserId: string;
  reason: string;
  note?: string | null;
  status: string;
  createdAt: Timestamp;
  appeal?: AppealData;
}

// Reported-user side: suspension record read off the signed-in user's own
// users/{uid}/private/socialBlockDetails doc. Distinct from MyReport/appeal
// above, which is the reporter-side flow on the `reports` collection.
interface SocialBlockDetails {
  isBlocked?: boolean;
  blockedReason?: string;
  blockedReasonCategory?: "legal" | "community";
  blockedAt?: Timestamp;
  blockedDuration?: number | null;
  appeal?: AppealData;
}

const STATUS_COLOR_CLASSES: Record<ReportStatusOption["color"], string> = {
  blue: "bg-blue-500/10 text-blue-700 border-blue-200",
  gray: "bg-base-300 text-base-content/70 border-base-300",
  red: "bg-red-500/10 text-red-700 border-red-200",
  green: "bg-green-500/10 text-green-700 border-green-200",
  orange: "bg-orange-500/10 text-orange-700 border-orange-200",
  purple: "bg-purple-500/10 text-purple-700 border-purple-200",
};

const DAY_MS = 86_400_000;

const isSuspensionActive = (b?: SocialBlockDetails | null): boolean => {
  if (!b || !b.isBlocked) return false;
  if (b.blockedDuration == null) return true;
  const startMs = b.blockedAt?.toMillis?.() ?? 0;
  return startMs + b.blockedDuration * DAY_MS > Date.now();
};

const MyReportsPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [reports, setReports] = useState<MyReport[]>([]);
  const [statusOptions, setStatusOptions] = useState<ReportStatusOption[]>([]);
  const [reasonOptions, setReasonOptions] = useState<ReportOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [showHelpDialog, setShowHelpDialog] = useState(false);

  // Reporter-side appeal (appealing a report you filed)
  const [appealingReportId, setAppealingReportId] = useState<string | null>(null);
  const [appealNote, setAppealNote] = useState("");
  const [submittingAppeal, setSubmittingAppeal] = useState(false);

  // Reported-side: this account's own suspension status + its appeal
  const [socialBlock, setSocialBlock] = useState<SocialBlockDetails | null>(null);
  const [showSuspensionAppealDialog, setShowSuspensionAppealDialog] = useState(false);
  const [suspensionAppealNote, setSuspensionAppealNote] = useState("");
  const [submittingSuspensionAppeal, setSubmittingSuspensionAppeal] = useState(false);

  const [toast, setToast] = useState<{ open: boolean; type: ToastType; message: string }>({
    open: false,
    type: "success",
    message: "",
  });

  const showToast = (type: ToastType, message: string) => {
    setToast({ open: true, type, message });
    setTimeout(() => setToast((prev) => ({ ...prev, open: false })), 3000);
  };

  const fetchOptions = async () => {
    try {
      const functions = getFunctions();
      const getReportOptions = httpsCallable(functions, "getReportOptions");
      const result = await getReportOptions();
      const data = result.data as { reasons: ReportOption[]; statuses: ReportStatusOption[] };
      setReasonOptions(data.reasons ?? []);
      setStatusOptions(data.statuses ?? []);
    } catch (error) {
      console.error("Error fetching report options:", error);
    }
  };

  const fetchMyReports = async () => {
    if (!currentUser?.uid) return;
    try {
      setLoading(true);
      const reportsQuery = query(
        collection(db, "reports"),
        where("reporterId", "==", currentUser.uid),
      );
      const snap = await getDocs(reportsQuery);
      const rawReports = snap.docs.map(
        (d) => ({ id: d.id, ...(d.data() as Omit<MyReport, "id">) }),
      );
      // Sort client-side (newest first) to avoid needing a composite index.
      rawReports.sort((a, b) => (b.createdAt?.toMillis() ?? 0) - (a.createdAt?.toMillis() ?? 0));
      setReports(rawReports);
    } catch (error) {
      console.error("Error fetching my reports:", error);
      showToast("error", "Failed to load your reports.");
    } finally {
      setLoading(false);
    }
  };

  const fetchMySocialBlockStatus = async () => {
    if (!currentUser?.uid) return;
    try {
      const blockSnap = await getDoc(
        doc(db, "users", currentUser.uid, "private", "socialBlockDetails"),
      );
      setSocialBlock((blockSnap.data() as SocialBlockDetails) ?? null);
    } catch (error) {
      console.error("Error fetching suspension status:", error);
    }
  };

  useEffect(() => {
    fetchOptions();
    fetchMyReports();
    fetchMySocialBlockStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid]);

  const getStatusStyle = (statusValue: string) => {
    const match = statusOptions.find((s) => s.value === statusValue);
    return match ? STATUS_COLOR_CLASSES[match.color] : "bg-base-300 text-base-content/70 border-base-300";
  };

  const getStatusLabel = (statusValue: string) =>
    statusOptions.find((s) => s.value === statusValue)?.label ?? statusValue;

  const getReasonLabel = (reasonValue: string) =>
    reasonOptions.find((r) => r.value === reasonValue)?.label ?? reasonValue;

  const formatDate = (timestamp: Timestamp) => {
    if (!timestamp?.toDate) return "—";
    return timestamp.toDate().toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // --- Reporter-side appeal (unchanged flow) ---

  const openAppealDialog = (reportId: string) => {
    setAppealingReportId(reportId);
    setAppealNote("");
  };

  const closeAppealDialog = () => {
    setAppealingReportId(null);
    setAppealNote("");
  };

  const handleSubmitAppeal = async () => {
    if (!appealingReportId || !appealNote.trim()) return;

    try {
      setSubmittingAppeal(true);
      const functions = getFunctions();
      const submitReportAppeal = httpsCallable(functions, "submitReportAppeal");
      await submitReportAppeal({ reportId: appealingReportId, note: appealNote.trim() });

      // Reflect the appeal locally so the button switches to "Appeal submitted"
      // without a full refetch.
      setReports((prev) =>
        prev.map((r) =>
          r.id === appealingReportId
            ? {
                ...r,
                appeal: {
                  note: appealNote.trim(),
                  submittedAt: Timestamp.now(),
                  status: "pending",
                },
              }
            : r,
        ),
      );

      showToast("success", "Your appeal has been submitted.");
      closeAppealDialog();
    } catch (error: any) {
      console.error("Error submitting appeal:", error);
      showToast("error", error?.message || "Failed to submit appeal. Please try again.");
    } finally {
      setSubmittingAppeal(false);
    }
  };

  // --- Reported-side appeal (new: appealing this account's own suspension) ---

  const openSuspensionAppealDialog = () => {
    setSuspensionAppealNote("");
    setShowSuspensionAppealDialog(true);
  };

  const closeSuspensionAppealDialog = () => {
    setShowSuspensionAppealDialog(false);
    setSuspensionAppealNote("");
  };

  const handleSubmitSuspensionAppeal = async () => {
    if (!suspensionAppealNote.trim()) return;

    try {
      setSubmittingSuspensionAppeal(true);
      const functions = getFunctions();
      const submitSuspensionAppeal = httpsCallable(functions, "submitSuspensionAppeal");
      await submitSuspensionAppeal({ note: suspensionAppealNote.trim() });

      setSocialBlock((prev) =>
        prev
          ? {
              ...prev,
              appeal: {
                note: suspensionAppealNote.trim(),
                submittedAt: Timestamp.now(),
                status: "pending",
              },
            }
          : prev,
      );

      showToast("success", "Your appeal has been submitted.");
      closeSuspensionAppealDialog();
    } catch (error: any) {
      console.error("Error submitting suspension appeal:", error);
      showToast("error", error?.message || "Failed to submit appeal. Please try again.");
    } finally {
      setSubmittingSuspensionAppeal(false);
    }
  };

  const suspensionActive = isSuspensionActive(socialBlock);
  const hasSuspensionHistory = !!socialBlock?.isBlocked;

  return (
    <div className="w-full pt-16">
      <Header />
      <Toast
        open={toast.open}
        type={toast.type}
        message={toast.message}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
      />
      <LoaderBackdrop open={loading} />

      <div className="container max-w-2xl mx-auto py-8 px-8 flex flex-col">
        <div className="flex items-center justify-between pb-4">
          <div className="flex items-center">
            <button
              onClick={() => navigate("/settings")}
              className="flex items-center p-2 bg-base-300 rounded-full hover:bg-base-300 transition-colors"
            >
              <HiChevronLeftSolid className="h-6 w-6 text-base-content" />
            </button>
            <h1 className="text-3xl font-semibold text-base-content ml-4">My Reports</h1>
          </div>
          <button
            onClick={() => setShowHelpDialog(true)}
            className="p-1 text-base-content/60 hover:text-base-content/80 transition-colors"
            aria-label="Information about My Reports"
          >
            <QuestionMarkCircleIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Account Status — reported-user side. Only rendered if this
            account has ever been suspended (past or present). */}
        {hasSuspensionHistory && socialBlock && (
          <div className="mb-6">
            <h2 className="text-sm font-semibold text-base-content/70 uppercase tracking-wide mb-2">
              Account Status
            </h2>
            <div className="bg-base-100 rounded-lg p-6">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <h3 className="text-base font-semibold text-base-content">
                    Social features {suspensionActive ? "suspended" : "restored"}
                  </h3>
                  {suspensionActive && (
                    <p className="text-xs text-base-content/60 mt-0.5">
                      Effective{" "}
                      {socialBlock.blockedAt?.toDate
                        ? socialBlock.blockedAt.toDate().toLocaleDateString("en-GB", {
                            day: "numeric",
                            month: "long",
                            year: "numeric",
                          })
                        : "—"}
                    </p>
                  )}
                </div>
                {suspensionActive && (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border whitespace-nowrap bg-red-500/10 text-red-700 border-red-200">
                    {socialBlock.blockedDuration == null
                      ? "Permanent"
                      : `${socialBlock.blockedDuration} days`}
                  </span>
                )}
              </div>

              {suspensionActive && (
                <p className="text-sm text-base-content/70 mb-3">
                  <span className="font-medium text-base-content/80">Reason: </span>
                  {getReasonLabel(socialBlock.blockedReason || "")} (
                  {socialBlock.blockedReasonCategory === "legal"
                    ? "Legal basis"
                    : "Community Guidelines"}
                  )
                </p>
              )}

              {suspensionActive && (
                <div className="pt-3 border-t border-base-300">
                  {socialBlock.appeal ? (
                    <div className="text-sm text-base-content/70">
                      <p className="font-medium text-base-content/80 mb-1">
                        Appeal {socialBlock.appeal.status === "pending" ? "submitted" : socialBlock.appeal.status}
                      </p>
                      <p>{socialBlock.appeal.note}</p>
                    </div>
                  ) : (
                    <button
                      onClick={openSuspensionAppealDialog}
                      className="text-sm font-medium text-primary hover:underline"
                    >
                      Appeal this decision
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        <h2 className="text-sm font-semibold text-base-content/70 uppercase tracking-wide mb-2">
          Reports I've Filed
        </h2>
        <div className="flex flex-col gap-4">
          {!loading && reports.length === 0 && (
            <div className="bg-base-100 rounded-lg p-6 text-center">
              <p className="text-sm text-base-content/70">
                You haven't reported any posts or users yet.
              </p>
            </div>
          )}

          {reports.map((report) => (
            <div key={report.id} className="bg-base-100 rounded-lg p-6">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <h3 className="text-base font-semibold text-base-content">
                    {getReasonLabel(report.reason)}
                  </h3>
                  <p className="text-xs text-base-content/60 mt-0.5">
                    Reported on {formatDate(report.createdAt)}
                  </p>
                </div>
                <span
                  className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border whitespace-nowrap ${getStatusStyle(report.status)}`}
                >
                  {getStatusLabel(report.status)}
                </span>
              </div>

              {report.note && (
                <p className="text-sm text-base-content/70 mb-3">
                  <span className="font-medium text-base-content/80">Your note: </span>
                  {report.note}
                </p>
              )}

              {report.status === "closed" && (
                <div className="pt-3 border-t border-base-300">
                  {report.appeal ? (
                    <div className="text-sm text-base-content/70">
                      <p className="font-medium text-base-content/80 mb-1">Appeal submitted</p>
                      <p>{report.appeal.note}</p>
                    </div>
                  ) : (
                    <button
                      onClick={() => openAppealDialog(report.id)}
                      className="text-sm font-medium text-primary hover:underline"
                    >
                      Appeal this decision
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Reporter-side appeal dialog (existing flow, unchanged) */}
        {appealingReportId && (
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-base-100 rounded-lg max-w-md w-full">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-grotesk font-semibold text-base-content">
                    Submit an Appeal
                  </h3>
                  <button
                    onClick={closeAppealDialog}
                    className="p-1 rounded-full hover:bg-base-200 transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5 text-base-content/60" />
                  </button>
                </div>
                <p className="text-sm text-base-content/70 mb-4">
                  Let us know why you think this decision should be reconsidered.
                </p>
                <textarea
                  value={appealNote}
                  onChange={(e) => setAppealNote(e.target.value)}
                  placeholder="Explain why you're appealing this decision..."
                  className="w-full border border-base-300 rounded-md p-2 text-sm bg-base-100 text-base-content"
                  rows={4}
                  maxLength={1000}
                />
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={closeAppealDialog}
                    disabled={submittingAppeal}
                    className="flex-1 bg-base-200 text-base-content font-grotesk px-4 py-2 rounded-md hover:opacity-80 transition-opacity disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmitAppeal}
                    disabled={submittingAppeal || !appealNote.trim()}
                    className="flex-1 bg-neutral text-white font-grotesk px-4 py-2 rounded-md hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submittingAppeal ? "Submitting..." : "Submit Appeal"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Reported-side appeal dialog (new: appealing this account's suspension) */}
        {showSuspensionAppealDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-base-100 rounded-lg max-w-md w-full">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-grotesk font-semibold text-base-content">
                    Appeal Suspension
                  </h3>
                  <button
                    onClick={closeSuspensionAppealDialog}
                    className="p-1 rounded-full hover:bg-base-200 transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5 text-base-content/60" />
                  </button>
                </div>
                <p className="text-sm text-base-content/70 mb-4">
                  Let us know why you think this suspension should be reconsidered.
                </p>
                <textarea
                  value={suspensionAppealNote}
                  onChange={(e) => setSuspensionAppealNote(e.target.value)}
                  placeholder="Explain why you're appealing this suspension..."
                  className="w-full border border-base-300 rounded-md p-2 text-sm bg-base-100 text-base-content"
                  rows={4}
                  maxLength={1000}
                />
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={closeSuspensionAppealDialog}
                    disabled={submittingSuspensionAppeal}
                    className="flex-1 bg-base-200 text-base-content font-grotesk px-4 py-2 rounded-md hover:opacity-80 transition-opacity disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmitSuspensionAppeal}
                    disabled={submittingSuspensionAppeal || !suspensionAppealNote.trim()}
                    className="flex-1 bg-neutral text-white font-grotesk px-4 py-2 rounded-md hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submittingSuspensionAppeal ? "Submitting..." : "Submit Appeal"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Help dialog */}
        {showHelpDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-base-100 rounded-lg max-w-lg w-full max-h-[80vh] overflow-y-auto">
              <div className="p-8">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-grotesk font-semibold text-base-content">
                    My Reports
                  </h3>
                  <button
                    onClick={() => setShowHelpDialog(false)}
                    className="p-1 rounded-full hover:bg-base-200 transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5 text-base-content/60" />
                  </button>
                </div>
                <div className="space-y-4 text-base-content/80 text-sm">
                  <p>
                    This is a list of posts/users you've reported, along with the current
                    status of each report.
                  </p>
                  <p>
                    <strong>Received:</strong> we've got your report and it's in the queue.
                  </p>
                  <p>
                    <strong>Under Review:</strong> our team is actively reviewing it.
                  </p>
                  <p>
                    <strong>Action Taken / No Action Taken:</strong> we've made a decision
                    based on our review.
                  </p>
                  <p>
                    <strong>Closed:</strong> the report has been resolved. If you disagree
                    with the outcome, you can submit an appeal.
                  </p>
                  <p>
                    If your own account has been suspended from social features, you'll see
                    an "Account Status" section above, with the option to appeal that
                    decision as well.
                  </p>
                </div>
                <div className="pt-6 -mx-8 px-8 mt-2">
                  <button
                    onClick={() => setShowHelpDialog(false)}
                    className="w-full bg-neutral text-white font-grotesk px-4 py-2 rounded-md hover:bg-neutral/90 transition-colors"
                  >
                    Okay
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyReportsPage;